import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../firebase';
import './ShareTaskModal.css';

interface ShareTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  onShareSuccess: (email: string, userId: string, photoURL?: string) => void;
}

const SHARED_EMAILS_KEY = 'stackly_shared_emails';

export const ShareTaskModal: React.FC<ShareTaskModalProps> = ({
  isOpen,
  onClose,
  taskId,
  onShareSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState('');
  const [previousEmails, setPreviousEmails] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load previously shared emails from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(SHARED_EMAILS_KEY);
    if (stored) {
      try {
        const emails = JSON.parse(stored);
        if (Array.isArray(emails)) {
          setPreviousEmails(emails);
        }
      } catch (error) {
        console.error('Error loading shared emails:', error);
      }
    }
  }, []);

  // Save email to localStorage
  const saveEmailToHistory = (emailToSave: string) => {
    const stored = localStorage.getItem(SHARED_EMAILS_KEY);
    let emails: string[] = [];

    if (stored) {
      try {
        emails = JSON.parse(stored);
        if (!Array.isArray(emails)) {
          emails = [];
        }
      } catch (error) {
        emails = [];
      }
    }

    // Add email if not already in list
    if (!emails.includes(emailToSave)) {
      emails.unshift(emailToSave); // Add to beginning
      // Keep only the last 10 emails
      emails = emails.slice(0, 10);
      localStorage.setItem(SHARED_EMAILS_KEY, JSON.stringify(emails));
      setPreviousEmails(emails);
    }
  };

  // Filter suggestions based on input
  const filteredSuggestions = previousEmails.filter(prevEmail =>
    prevEmail.toLowerCase().includes(email.toLowerCase()) && prevEmail.toLowerCase() !== email.toLowerCase()
  );

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    setIsSharing(true);
    setError('');

    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        setError('You must be logged in to share tasks');
        setIsSharing(false);
        return;
      }

      // Check if trying to share with yourself
      if (email.toLowerCase() === currentUser.email.toLowerCase()) {
        setError('You cannot share a task with yourself');
        setIsSharing(false);
        return;
      }

      // Check if user exists
      const checkResponse = await fetch('/api/check-user-exists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });

      if (!checkResponse.ok) {
        setError(`Server error: ${checkResponse.status}. Try clearing your browser cookies for localhost.`);
        setIsSharing(false);
        return;
      }

      const checkData = await checkResponse.json();

      if (!checkData.exists) {
        setError('This user must sign up for Stackly before you can share a task with them');
        setIsSharing(false);
        return;
      }

      // Share the task
      const shareResponse = await fetch('/api/share-task', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          taskId,
          ownerUserId: currentUser.uid,
          ownerEmail: currentUser.email,
          inviteeEmail: email.toLowerCase(),
          inviteeUserId: checkData.userId,
          inviteePhotoURL: checkData.photoURL,
        }),
      });

      if (!shareResponse.ok) {
        setError(`Server error: ${shareResponse.status}. Try clearing your browser cookies for localhost.`);
        setIsSharing(false);
        return;
      }

      const shareData = await shareResponse.json();

      if (!shareData.success) {
        setError(shareData.message || 'Failed to share task');
        setIsSharing(false);
        return;
      }

      // Success!
      saveEmailToHistory(email.toLowerCase());
      onShareSuccess(email.toLowerCase(), checkData.userId, checkData.photoURL);
      setEmail('');
      setShowSuggestions(false);
      onClose();
    } catch (error) {
      console.error('Error sharing task:', error);
      setError('An error occurred while sharing the task');
    } finally {
      setIsSharing(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setShowSuggestions(false);
    onClose();
  };

  const handleSuggestionClick = (suggestion: string) => {
    setEmail(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content share-task-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Share Task</h2>
          <button className="modal-close-btn" onClick={handleClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleShare}>
          <div className="share-form-content">
            <label htmlFor="share-email">Enter email address:</label>
            <div className="share-email-input-container">
              <input
                id="share-email"
                type="email"
                className="share-email-input"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  // Delay hiding suggestions to allow click events
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                placeholder="colleague@example.com"
                autoFocus
                disabled={isSharing}
                ref={inputRef}
              />
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="share-email-suggestions">
                  {filteredSuggestions.map(suggestion => (
                    <div
                      key={suggestion}
                      className="share-email-suggestion"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {error && <div className="share-error-message">{error}</div>}
          </div>
          <div className="share-modal-actions">
            <button
              type="button"
              className="share-cancel-btn"
              onClick={handleClose}
              disabled={isSharing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="share-submit-btn"
              disabled={isSharing || !email.trim()}
            >
              {isSharing ? 'Sharing...' : 'Share'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
